import { useCallback, useEffect, useState } from "react";
import { Link2, Link2Off, Loader2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import {
  TEAM_LINK_MAX_REASON,
  createTeamLink,
  endTeamLink,
  getTeamLinks,
  type TeamLinks,
} from "@/lib/tickets";
import { useZone } from "@/components/session-provider";
import { formatDate } from "@/lib/time";
import { ConfirmPanel } from "@/components/Confirm";

/**
 * Teams that work each other's requests (D-97).
 *
 * <p>
 * ⚠️ **THE SCREEN'S JOB IS TO SAY WHAT A LINK WILL AND WILL NOT REACH**, and that is why it
 * prints classifications rather than just a partner name. A link confers reach and confers no
 * clearance: linking a Standard-only team to one whose categories are Clinical produces an
 * arrangement that is live, correct, saved and audited — and shows those agents nothing at all.
 * Without the two lists below, the screen would report a working link over an empty queue and
 * nobody would be able to tell which of the two was wrong.
 * </p>
 *
 * <p>
 * ⚠️ **It also says what a link is not.** Linked agents are not members: they get no clearance,
 * and they are on none of the partner's notification lists — which is a property of how audiences
 * are computed (from `TeamMemberships`, which a link does not write) rather than a setting
 * anybody chose. Stated on screen because "they can see our queue" invites the assumption that
 * they are now on the team.
 * </p>
 *
 * <p>
 * ⚠️ Nothing here decides anything. Creating a link requires `team.manage` over BOTH teams and
 * the server answers 403 with an explanation when the caller administers only one — the partner
 * was picked from a list, so a 404 would be a lie that reads as a bug. Ending one needs authority
 * over one side, because ending can only ever narrow access.
 * </p>
 */
export function TeamLinksPanel({ teamId }: { teamId: string }) {
  const zone = useZone();

  const [state, setState] = useState<TeamLinks | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [partnerId, setPartnerId] = useState("");
  const [reason, setReason] = useState("");
  const [ending, setEnding] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setState(await getTeamLinks(teamId));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "The links could not be loaded.");
    }
  }, [teamId]);

  useEffect(() => {
    setState(null);
    setError(null);
    setPartnerId("");
    setReason("");
    setEnding(null);
    void load();
  }, [load]);

  async function link() {
    if (busy) return;

    // ⚠️ Checked here so the message is specific and checked again on the server, which is what
    // enforces it. A disabled button is not validation (D-44).
    if (!partnerId) {
      setError("Choose a team to link to.");
      return;
    }

    if (reason.trim().length === 0) {
      setError("Say why these teams are covering for each other. It is recorded on the link.");
      return;
    }

    setBusy(true);
    setError(null);

    try {
      await createTeamLink(teamId, partnerId, reason.trim());
      setPartnerId("");
      setReason("");
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That link could not be created.");
    } finally {
      setBusy(false);
    }
  }

  async function end(linkId: string) {
    setBusy(true);
    setError(null);

    try {
      await endTeamLink(teamId, linkId);
      setEnding(null);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That link could not be ended.");
    } finally {
      setBusy(false);
    }
  }

  if (state === null) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading…
      </p>
    );
  }

  return (
    <div className="space-y-5">
      {/* ⚠️ The explanation is above the controls rather than in a tooltip. What a link does is
          not guessable from the word, and the two things it does NOT do — clearance and
          notifications — are exactly what somebody would otherwise assume it did. */}
      <div className="rounded-lg border border-border bg-secondary/30 p-3 text-xs text-muted-foreground">
        <p className="font-medium text-foreground">What a link does</p>
        <ul className="mt-1.5 space-y-1">
          <li>
            Both teams' agents can read, reply on, assign, resolve and pass on each other's requests
            — as if they were on the other team's queue.
          </li>
          <li>
            <span className="font-medium text-foreground">It grants no clearance.</span> A
            classification {state.teamName} is not cleared for stays invisible to them, exactly as
            it is now. Clearance is set on the team itself, deliberately and separately.
          </li>
          <li>
            <span className="font-medium text-foreground">It grants no membership.</span> Linked
            agents get none of the other team's notifications — escalations, low ratings, arriving
            requests. They find work by looking at the queue.
          </li>
          <li>Either team's administrator can end it. Creating one needs authority over both.</li>
        </ul>
      </div>

      <div>
        <h3 className="text-sm font-medium">Linked teams</h3>

        {state.active.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            {state.teamName} is not linked to any other team.
          </p>
        ) : (
          <ul className="mt-2 space-y-2">
            {state.active.map((linkRow) => (
              <li key={linkRow.id} className="rounded-lg border border-border bg-card p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="flex items-center gap-1.5 text-sm font-medium">
                      <Link2 className="h-3.5 w-3.5 text-primary" aria-hidden />
                      {linkRow.partnerTeamName}
                    </p>

                    <p className="mt-0.5 text-xs text-muted-foreground">{linkRow.reason}</p>

                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      Linked by {linkRow.createdByName} on {formatDate(linkRow.createdAtUtc, zone)}
                    </p>

                    {/* ⚠️ THE HONEST PART. Reach without clearance is a live link that shows
                        nothing, and this is the only place anybody would find that out before
                        an agent reports an empty queue. Classifications are NAMED rather than
                        counted (D-52's rule for the reporting scope notice): a count tells an
                        administrator something is hidden without telling them what to do. */}
                    {linkRow.sharedClassifications.length > 0 && (
                      <p className="mt-1.5 text-[11px] text-muted-foreground">
                        {state.teamName} can read{" "}
                        <span className="font-medium text-foreground">
                          {linkRow.sharedClassifications.join(", ")}
                        </span>{" "}
                        requests on {linkRow.partnerTeamName}'s queue.
                      </p>
                    )}

                    {linkRow.unreachableClassifications.length > 0 && (
                      <p className="mt-1 text-[11px] text-warning-foreground">
                        {linkRow.sharedClassifications.length === 0 ? (
                          <>
                            ⚠️ This link currently reaches{" "}
                            <span className="font-medium">nothing</span>: every category on{" "}
                            {linkRow.partnerTeamName}'s queue is{" "}
                            {linkRow.unreachableClassifications.join(" or ")}, and {state.teamName}{" "}
                            is not cleared for those. Give {state.teamName} that clearance on its
                            own record if this link is meant to work.
                          </>
                        ) : (
                          <>
                            Not{" "}
                            <span className="font-medium">
                              {linkRow.unreachableClassifications.join(", ")}
                            </span>{" "}
                            requests — {state.teamName} holds no clearance for those.
                          </>
                        )}
                      </p>
                    )}
                  </div>

                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setEnding(ending === linkRow.id ? null : linkRow.id)}
                    className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-input px-2.5 text-xs transition hover:bg-accent disabled:opacity-50"
                  >
                    <Link2Off className="h-3.5 w-3.5" aria-hidden />
                    End link
                  </button>
                </div>

                {/* ⚠️ Asks first, like every administrative action here. It is reversible — the
                    link can be made again — and administration confirms reversible actions
                    anyway, on the reasoning that reversible is no comfort to the agents who lose
                    a queue mid-shift. */}
                {ending === linkRow.id && (
                  <div className="mt-2">
                    <ConfirmPanel
                      tone="warning"
                      question={`End the link between ${state.teamName} and ${linkRow.partnerTeamName}?`}
                      detail={`Neither team's agents will reach the other's requests through it from then on — including anything they are part-way through. Requests already assigned to somebody stay assigned; they may simply no longer be able to open them. Either team's administrator can link them again.`}
                      confirmLabel="End link"
                      onConfirm={() => end(linkRow.id)}
                      onCancel={() => setEnding(null)}
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h3 className="text-sm font-medium">Link to another team</h3>

        {state.candidates.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            There is no other active team left to link to.
          </p>
        ) : (
          <div className="mt-2 space-y-2">
            <div>
              <label
                htmlFor="team-link-partner"
                className="text-xs font-medium text-muted-foreground"
              >
                Team
              </label>
              <select
                id="team-link-partner"
                value={partnerId}
                disabled={busy}
                onChange={(e) => {
                  setPartnerId(e.target.value);
                  setError(null);
                }}
                className="mt-1 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/30 disabled:opacity-50 sm:max-w-xs"
              >
                <option value="">Choose a team…</option>
                {state.candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="team-link-reason"
                className="text-xs font-medium text-muted-foreground"
              >
                Why
              </label>
              <input
                id="team-link-reason"
                value={reason}
                maxLength={TEAM_LINK_MAX_REASON}
                disabled={busy}
                onChange={(e) => {
                  setReason(e.target.value);
                  setError(null);
                }}
                placeholder="EMR and IT cover for each other out of hours"
                className="mt-1 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/30 disabled:opacity-50"
              />

              {/* ⚠️ Required, like a break-glass justification and an export's, and for the same
                  reason: this widens who reads a team's PHI-bearing requests, and "who decided
                  this and why" is the question an audit asks six months later when everybody who
                  was in the room has forgotten. */}
              <p className="mt-1 text-[11px] text-muted-foreground">
                Recorded on the link and in the audit log. Say what the arrangement is for.
              </p>
            </div>

            <button
              type="button"
              onClick={() => void link()}
              disabled={busy}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-gradient-primary px-4 text-sm font-medium text-primary-foreground shadow-glow transition hover:opacity-90 disabled:opacity-50"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              Link teams
            </button>
          </div>
        )}
      </div>

      {/* ⚠️ Ended links are kept and shown. The record that one existed is what explains why a
          colleague's name is on requests belonging to a team they were never a member of — which
          is exactly the question somebody reading an old request will ask. */}
      {state.ended.length > 0 && (
        <div>
          <h3 className="text-sm font-medium">Past links</h3>
          <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
            {state.ended.map((linkRow) => (
              <li key={linkRow.id}>
                <span className="font-medium text-foreground">{linkRow.partnerTeamName}</span> —{" "}
                {formatDate(linkRow.createdAtUtc, zone)} to{" "}
                {linkRow.revokedAtUtc ? formatDate(linkRow.revokedAtUtc, zone) : "—"} ·{" "}
                {linkRow.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
